import React, { useState, useEffect, useMemo } from "react";
import {
  Input, Row, Card, Table, Spin, Button, Space, Form, Pagination,
} from "antd";
import { ArrowDownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import { labelConfig, staticConfig, apiConfig } from "../treasuryContractBooking.config";
import SearchLg from "@untitled-ui/icons-react/build/esm/SearchLg";
import ArrowLeft from "@untitled-ui/icons-react/build/esm/ArrowLeft";
import ArrowRight from "@untitled-ui/icons-react/build/esm/ArrowRight";
import { BOM } from "../../../services/definedMethod";

const DUMMY_BOND_CONTRACTS = [
  { id: 1, accountNo: "BOND-2026-001", clientId: 1, productId: 2, status: "ACTIVE",    faceValue: 100_000_000, purchasePrice: 98_500_000,  issueDate: "2026-02-24", maturityDate: "2031-02-24", tenor: 1825, couponRate: 13.5, couponFrequency: "SEMI_ANNUAL", settlementId: 2, tradeId: 2 },
  { id: 2, accountNo: "BOND-2026-002", clientId: 2, productId: 2, status: "PENDING",   faceValue: 200_000_000, purchasePrice: 196_000_000, issueDate: "2026-03-01", maturityDate: "2029-03-01", tenor: 1095, couponRate: 14.0, couponFrequency: "QUARTERLY",    settlementId: 3, tradeId: 3 },
  { id: 3, accountNo: "BOND-2026-003", clientId: 3, productId: 3, status: "MATURED",   faceValue: 50_000_000,  purchasePrice: 49_000_000,  issueDate: "2021-01-15", maturityDate: "2026-01-15", tenor: 1826, couponRate: 12.0, couponFrequency: "ANNUAL",       settlementId: 4, tradeId: 4 },
  { id: 4, accountNo: "BOND-2026-004", clientId: 4, productId: 2, status: "ACTIVE",    faceValue: 500_000_000, purchasePrice: 492_500_000, issueDate: "2026-03-15", maturityDate: "2033-03-15", tenor: 2557, couponRate: 15.5, couponFrequency: "SEMI_ANNUAL",  settlementId: 5, tradeId: 5 },
  { id: 5, accountNo: "BOND-2026-005", clientId: 1, productId: 3, status: "REJECTED",  faceValue: 75_000_000,  purchasePrice: 73_500_000,  issueDate: "2026-03-22", maturityDate: "2028-03-22", tenor: 730,  couponRate: 11.0, couponFrequency: "QUARTERLY",    settlementId: 6, tradeId: 6 },
];

const STATUS_COLORS = {
  ACTIVE:   { bg: "#ECFDF3", color: "#027A48" },
  PENDING:  { bg: "#FFFAEB", color: "#B54708" },
  MATURED:  { bg: "#EFF8FF", color: "#175CD3" },
  REJECTED: { bg: "#FEF3F2", color: "#B42318" },
  CLOSED:   { bg: "#F2F4F7", color: "#344054" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || { bg: "#F2F4F7", color: "#344054" };
  return <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 99, fontSize: 12, fontWeight: 600, background: s.bg, color: s.color }}>{status}</span>;
};

const fmt = (val) => val != null ? val.toLocaleString("en-NG", { minimumFractionDigits: 2 }) : "—";

const ManageBondContracts = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { rightBtn, rightAction } = props;

  const [currentPage,  setCurrentPage]  = useState(1);
  const [selectedRow,  setSelectedRow]  = useState([]);
  const [searchTerm,   setSearchTerm]   = useState("");
  const [originalList, setOriginalList] = useState([]);
  const [loading,      setLoading]      = useState(false);
  const pageSize = 10;

  const columns = [
    {
      title: <div style={{ display: "flex", alignItems: "center", gap: 4 }}>Account No <ArrowDownOutlined style={{ fontSize: 14, color: "gray" }} /></div>,
      dataIndex: "accountNo",
      sorter: (a, b) => a.accountNo.localeCompare(b.accountNo),
    },
    { title: "Client ID",        dataIndex: "clientId" },
    { title: "Product ID",       dataIndex: "productId" },
    { title: "Issue Date",       dataIndex: "issueDate" },
    { title: "Maturity Date",    dataIndex: "maturityDate" },
    { title: "Tenor (Days)",     dataIndex: "tenor" },
    { title: "Face Value",       dataIndex: "faceValue",       render: fmt },
    { title: "Purchase Price",   dataIndex: "purchasePrice",   render: fmt },
    { title: "Coupon Rate",      dataIndex: "couponRate",      render: (v) => v != null ? `${v}%` : "—" },
    { title: "Coupon Frequency", dataIndex: "couponFrequency", render: (v) => v?.replace("_", " ") || "—" },
    { title: "Status",           dataIndex: "status",          render: (v) => <StatusBadge status={v} /> },
  ];

  useEffect(() => { getContractList(); }, []);
  useEffect(() => {
    if (BOM.HasPermission?.("CREATE_BOND_CONTRACT")) rightAction(cnt.RIGHT_ADD_BTN);
  }, []);

  const getContractList = () => {
    setLoading(true);
    // ── Swap for real API when ready ──
    // BOM.FetchReqAction({ session }, apiConfig.GetBondContracts, (err, res) => { ... });
    setTimeout(() => {
      setOriginalList(DUMMY_BOND_CONTRACTS.map((el) => ({ key: el.id, ...el })));
      setSelectedRow([]);
      setCurrentPage(1);
      setLoading(false);
    }, 400);
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return originalList;
    const q = searchTerm.toLowerCase();
    return originalList.filter((item) =>
      item.accountNo?.toLowerCase().includes(q) ||
      item.status?.toLowerCase().includes(q) ||
      item.issueDate?.toLowerCase().includes(q) ||
      item.couponFrequency?.toLowerCase().includes(q) ||
      String(item.clientId).includes(q)
    );
  }, [originalList, searchTerm]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const itemRender = (_, type, el) => { if (type === "prev" || type === "next") return; return el; };

  const onSelectItemAction = (selectedRowKeys, selectedRows) => {
    if (!selectedRowKeys || selectedRowKeys.length !== 1) {
      setSelectedRow([]);
      dispatch({ type: cnt.CLEAR_RIGHT_BTN });
      if (BOM.HasPermission?.("CREATE_BOND_CONTRACT")) dispatch({ type: cnt.RIGHT_ADD_BTN });
      return;
    }
    setSelectedRow(selectedRows);
    if (BOM.HasPermission?.("READ_BOND_CONTRACT"))   dispatch({ type: cnt.RIGHT_DETAIL_BTN });
    if (BOM.HasPermission?.("UPDATE_BOND_CONTRACT")) dispatch({ type: cnt.RIGHT_EDIT_BTN });
  };

  const handleOnView   = () => { if (rightBtn?.detailBtn === "primary") navigate("/view-bond-contract",  { state: selectedRow[0]?.id }); };
  const handleOnCreate = () => { if (rightBtn?.addBtn    === "primary") navigate("/add-bond-contract"); };
  const handleOnEdit   = () => { if (rightBtn?.editBtn   === "primary") navigate("/edit-bond-contract", { state: selectedRow[0]?.id }); };

  return (
    <MainLayout isEnableRightSidebar onView={handleOnView} onAdd={handleOnCreate} onEdit={handleOnEdit} pageItems={staticConfig.manageBondContract}>
      <TopContent pageTitle={labelConfig.bondContract} createPageTitle={labelConfig.bondContractListing} />

      <Card style={{ border: "1px solid #EAECF0", borderRadius: 20, marginTop: 12 }}>
        <Spin spinning={loading}>
          <Row style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Form form={form}>
              <Space size={12}>
                <Input
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value || ""); setCurrentPage(1); }}
                  placeholder="Search bond contracts…"
                  prefix={<SearchLg width={18} color="#667085" />}
                  style={{ width: 350, borderRadius: 10 }}
                />
              </Space>
            </Form>
          </Row>

          <Table rowSelection={{ type: "checkbox", hideSelectAll: true, onChange: onSelectItemAction }} columns={columns} dataSource={paginatedData} pagination={false} />

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
            <Button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}><ArrowLeft /> Previous</Button>
            <Pagination current={currentPage} total={filteredData.length} pageSize={pageSize} showSizeChanger={false} itemRender={itemRender} onChange={setCurrentPage} />
            <Button disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}>Next <ArrowRight /></Button>
          </div>
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({ rightBtn: state.RightSideBtn });
export default connect(mapStateToProps, { rightAction })(ManageBondContracts);